// Format texte d'echange des seances (export / import de la page Seances).
//
// Une seance par ligne, champs separes par " | " :
//   notation | nom | categorie | type | modele | notes
// Seule la notation est obligatoire : une ligne qui ne contient qu'une
// notation (ancien format d'import) reste valide. Les lignes vides et
// celles qui commencent par "#" (commentaires, en-tete) sont ignorees.
//   - categorie : CORPS (defaut), SEANCE, COMPETITION, ECHAUFFEMENT, RECUPERATION
//   - type      : COURT, MOYEN, LONG, SEUIL, TEMPO, PYRAMIDE, COMBINEE,
//                 ENDURANCE ; vide = automatique (deduit de la notation)
//   - modele    : REPETITIONS, CONTINU, AUCUN ; vide = automatique
// Les modeles (composes d'autres seances) ne sont pas exportes.
import { CATEGORIES, categorieDe, type Categorie, type CodeCategorie } from './composition.ts';
import { TYPES_SEANCE, type TypeSeance } from './classification.ts';
import { type Seance } from './seancesApi.ts';

export const SEPARATEUR = ' | ';

export type LigneEchange = {
  notation: string;
  nom?: string;
  categorie: CodeCategorie;
  typeSeance?: string;
  modeleSeance?: string;
  notes?: string;
};

// Un champ ne doit contenir ni separateur ni retour a la ligne.
function nettoyer(valeur: string | null | undefined): string {
  return (valeur ?? '').replace(/\s*\r?\n\s*/g, ' ').replace(/\|/g, '/').trim();
}

/** Texte d'export des seances (hors modeles), avec un en-tete en commentaire. */
export function exporterSeances(seances: Seance[]): string {
  const lignes = seances
    .filter((s) => categorieDe(s) !== 'MODELE' && s.notation.trim())
    .map((s) => {
      const champs = [
        nettoyer(s.notation),
        nettoyer(s.nom),
        categorieDe(s),
        s.typeForce && s.typeSeance ? s.typeSeance : '',
        categorieDe(s) === 'CORPS' ? s.modeleSeance ?? '' : '',
        nettoyer(s.notes),
      ];
      // Champs vides en fin de ligne omis.
      while (champs.length > 1 && champs[champs.length - 1] === '') champs.pop();
      return champs.join(SEPARATEUR);
    });
  return [
    `# Séances exportées le ${new Date().toLocaleDateString('fr-FR')} — ${lignes.length} séance(s)`,
    '# notation | nom | catégorie | type | modèle | notes',
    ...lignes,
  ].join('\n') + '\n';
}

/** Lecture d'une ligne d'import ; null pour une ligne vide ou un commentaire. */
export function lireLigne(ligne: string): LigneEchange | null {
  const texte = ligne.trim();
  if (!texte || texte.startsWith('#')) return null;
  const [notation, nom, categorie, type, modele, ...notes] = texte.split('|').map((c) => c.trim());
  const cat = CATEGORIES.find((c: Categorie) => c.code === (categorie ?? '').toUpperCase() && c.code !== 'MODELE')?.code ?? 'CORPS';
  const typeValide = TYPES_SEANCE.find((t: TypeSeance) => t.code === (type ?? '').toUpperCase())?.code;
  const modeleValide = ['REPETITIONS', 'CONTINU', 'AUCUN'].find((m) => m === (modele ?? '').toUpperCase());
  return {
    notation,
    nom: nom || undefined,
    categorie: cat,
    typeSeance: typeValide,
    modeleSeance: cat === 'CORPS' ? modeleValide : undefined,
    notes: notes.join(' / ').trim() || undefined,
  };
}

/** Telecharge un texte sous forme de fichier. */
export function telecharger(texte: string, nomFichier: string): void {
  const url = URL.createObjectURL(new Blob([texte], { type: 'text/plain;charset=utf-8' }));
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
